import { useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import z from "zod";
import moment from "moment";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@cofano/cck";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import useCreateOrExistingCharterFormFields, { charterDefaultValues, CustomShip } from "view/charters/components/form/useCreateOrExistingCharterFormFields";
import { CreateOrExistingPurchaseCharterSchema } from "view/charters/utils/charter.schema";
import { CharterSelectOption } from "view/charters/components/selects/CharterSelect";
import { CharterSubtype } from "__generated__/enums";

type CreateOrExistingCharterSchemaInput = z.input<typeof CreateOrExistingPurchaseCharterSchema>
type CreateOrExistingCharterSchemaOutput = z.output<typeof CreateOrExistingPurchaseCharterSchema>

const CreateCharterDialog = ({
	open,
	onCreate,
	onClose,
	ship,
	start,
	end,
	defaultCharterSubtype,
}: {
	open: boolean;
	onCreate: (charter: CharterSelectOption) => void;
	onClose: () => void;
	ship?: CustomShip;
	start?: number;
	end?: number;
	defaultCharterSubtype?: CharterSubtype;
}) => {

	const getDefaultSubtype = () => {
		if (defaultCharterSubtype === CharterSubtype.Time) {
			return CharterSubtype.Time;
		} else if (defaultCharterSubtype === CharterSubtype.Contract) {
			return CharterSubtype.Contract;
		} else {
			return CharterSubtype.Spot;
		}
	};

	const defaultValues = {
		charter: {
			...charterDefaultValues,
			subtype: getDefaultSubtype() as CharterSubtype.Spot | CharterSubtype.Time | CharterSubtype.Contract,
		},
	};

	const formProps = useForm<CreateOrExistingCharterSchemaInput, unknown, CreateOrExistingCharterSchemaOutput>({
		resolver: zodResolver(CreateOrExistingPurchaseCharterSchema),
		defaultValues: defaultValues,
	});

	const { charterFields } = useCreateOrExistingCharterFormFields({
		control: formProps.control,
		setValue: formProps.setValue,
		watch: formProps.watch,
		formState: formProps.formState,
		ship: ship,
		customStart: start,
		customEnd: end,
		createContractDisabled: true,
		disableExistingSelectOption: true,
	});

	const handleOnClose = () => {
		onClose();
		formProps.reset(defaultValues);
	};

	const handleOnCreate = (data: CreateOrExistingCharterSchemaOutput) => {
		if (data.charter.subtype !== "EXISTING") {
			const validBetween = data.charter.new.validBetween;

			onCreate({
				id: 0,
				type: data.charter.type,
				subtype: data.charter.subtype,
				charterNumber: data.charter.new.charterNumber,
				creditor: {
					id: data.charter.new.creditor.id,
					name: data.charter.new.creditor.name,
				},
				debtor: {
					id: data.charter.new.debtor.id,
					name: data.charter.new.debtor.name,
				},
				validFrom: validBetween?.[0] ? moment(validBetween[0]).unix() * 1000 : null,
				validUntil: validBetween?.[1] ? moment(validBetween[1]).unix() * 1000 : null,
			});
		}
	};

	return (
		<Dialog open={open} onClose={handleOnClose} fullWidth>
			<DialogTitle>
				{intl.formatMessage({ id: "CREATE_NEW_CHARTER" })}
			</DialogTitle>

			<form>
				<DialogContent dividers>
					{charterFields}
				</DialogContent>

				<DialogActions>
					<Button onClick={formProps.handleSubmit((d) => handleOnCreate(d))} size="small">
						{intl.formatMessage({ id: "CREATE" })}
					</Button>

					<Button onClick={() => handleOnClose()} size="small" variant="outlined">
						{intl.formatMessage({ id: "CANCEL" })}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};

export default CreateCharterDialog;
